import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { formatDate } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { fetchCustomers, createCustomer } from '@/services/customers'
import type { Customer, CreateCustomerPayload } from '@/types/customers'
import {
  AlertCircle,
  Users,
  DollarSign,
  Search,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Phone,
  Plus,
} from 'lucide-react'

type SortField = 'full_name' | 'phone_number' | 'total_given' | 'total_debt' | 'created_at'
type SortDirection = 'asc' | 'desc' | null

function LoadingSkeleton() {
  return (
    <div className='space-y-2'>
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} className='h-16 w-full' />
      ))}
    </div>
  )
}

export default function Customers() {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const [customers, setCustomers] = useState<Customer[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Sheet state
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [formData, setFormData] = useState<CreateCustomerPayload>({
    full_name: '',
    phone_number: '',
  })

  // Filter state
  const [searchQuery, setSearchQuery] = useState('')

  // Sort state
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  useEffect(() => {
    const loadCustomers = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const data = await fetchCustomers()
        setCustomers(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load customers')
      } finally {
        setIsLoading(false)
      }
    }

    loadCustomers()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setIsCreating(true)
      setError(null)
      const newCustomer = await createCustomer(formData)
      setCustomers((prev) => [newCustomer, ...prev])
      setIsSheetOpen(false)
      setFormData({ full_name: '', phone_number: '' })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create customer')
    } finally {
      setIsCreating(false)
    }
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else if (sortDirection === 'desc') {
        setSortField('created_at')
        setSortDirection('desc')
      } else {
        setSortDirection('asc')
      }
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1 h-3 w-3" />
    }
    if (sortDirection === 'asc') {
      return <ArrowUp className="ml-1 h-3 w-3" />
    }
    if (sortDirection === 'desc') {
      return <ArrowDown className="ml-1 h-3 w-3" />
    }
    return <ArrowUpDown className="ml-1 h-3 w-3" />
  }

  const filteredCustomers = useMemo(() => {
    let result = customers.filter((customer) => {
      const searchLower = searchQuery.toLowerCase()
      const matchesSearch =
        searchQuery === '' ||
        (customer.full_name?.toLowerCase().includes(searchLower) ?? false) ||
        (customer.phone_number?.toLowerCase().includes(searchLower) ?? false)

      return matchesSearch
    })

    if (sortField && sortDirection) {
      result = [...result].sort((a, b) => {
        let aValue: string | number
        let bValue: string | number

        switch (sortField) {
          case 'full_name':
            aValue = a.full_name ?? ''
            bValue = b.full_name ?? ''
            break
          case 'phone_number':
            aValue = a.phone_number ?? ''
            bValue = b.phone_number ?? ''
            break
          case 'total_given':
            aValue = a.total_given
            bValue = b.total_given
            break
          case 'total_debt':
            aValue = a.total_debt
            bValue = b.total_debt
            break
          case 'created_at':
            aValue = new Date(a.created_at).getTime()
            bValue = new Date(b.created_at).getTime()
            break
          default:
            return 0
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue.localeCompare(bValue)
          return sortDirection === 'asc' ? comparison : -comparison
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1

        return 0
      })
    }

    return result
  }, [customers, searchQuery, sortField, sortDirection])

  const clearFilters = () => {
    setSearchQuery('')
  }

  const hasActiveFilters = searchQuery !== ''

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    }).format(amount)
  }

  const totalCustomers = customers.length
  const totalGivenMoney = customers.reduce((sum, c) => sum + c.total_given, 0)
  const totalLeftMoney = customers.reduce((sum, c) => sum + c.total_debt, 0)

  return (
    <>
      <header className='flex h-16 shrink-0 items-center gap-2'>
        <div className='flex items-center gap-2 px-4'>
          <SidebarTrigger className='-ml-1' />
          <Separator
            orientation='vertical'
            className='mr-2 data-[orientation=vertical]:h-4'
          />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>{t('customers.title')}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </header>

      {error && (
        <div className='mx-4 mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400'>
          <AlertCircle className='h-4 w-4' />
          <span>{error}</span>
        </div>
      )}

      <div className='flex flex-1 flex-col gap-4 p-4 pt-0'>
        {/* Summary Cards */}
        <div className='grid gap-4 md:grid-cols-3'>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>
                {t('customers.totalCustomers')}
              </CardTitle>
              <Users className='h-4 w-4 text-muted-foreground' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold'>{totalCustomers}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>
                {t('customers.totalGiven')}
              </CardTitle>
              <DollarSign className='h-4 w-4 text-green-500' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold text-green-600 dark:text-green-400'>
                {formatCurrency(totalGivenMoney)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>
                {t('customers.totalOwed')}
              </CardTitle>
              <DollarSign className='h-4 w-4 text-muted-foreground' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold'>
                {formatCurrency(totalLeftMoney)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Customers Table */}
        <Card>
          <CardHeader className='flex flex-row items-center justify-between'>
            <div>
              <CardTitle>{t('customers.title')}</CardTitle>
              <CardDescription>
                {t('customers.manageCustomers')}
              </CardDescription>
            </div>
            <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
              <SheetTrigger asChild>
                <Button>
                  <Plus className='mr-2 h-4 w-4' />
                  {t('customers.addCustomer')}
                </Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>{t('customers.createCustomer')}</SheetTitle>
                  <SheetDescription>
                    {t('customers.createCustomerDesc')}
                  </SheetDescription>
                </SheetHeader>
                <form onSubmit={handleSubmit} className='flex flex-col gap-4 p-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='full_name'>{t('customers.fullName')}</Label>
                    <Input
                      id='full_name'
                      placeholder={t('customers.enterFullName')}
                      value={formData.full_name}
                      onChange={(e) =>
                        setFormData({ ...formData, full_name: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='phone_number'>{t('customers.phoneNumber')}</Label>
                    <Input
                      id='phone_number'
                      type='tel'
                      placeholder='+998901234567'
                      value={formData.phone_number}
                      onChange={(e) =>
                        setFormData({ ...formData, phone_number: e.target.value })
                      }
                      required
                    />
                  </div>
                  <SheetFooter>
                    <Button type='submit' disabled={isCreating}>
                      {isCreating ? t('customers.creating') : t('common.create')}
                    </Button>
                  </SheetFooter>
                </form>
              </SheetContent>
            </Sheet>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className='mb-4 flex flex-wrap items-center gap-3'>
              <div className='relative flex-1 min-w-[200px]'>
                <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
                <Input
                  placeholder={t('customers.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className='pl-9'
                />
              </div>
              {hasActiveFilters && (
                <Button variant='ghost' size='sm' onClick={clearFilters}>
                  <X className='mr-1 h-4 w-4' />
                  {t('common.clear')}
                </Button>
              )}
            </div>

            {isLoading ? (
              <LoadingSkeleton />
            ) : filteredCustomers.length === 0 ? (
              <div className='py-8 text-center text-muted-foreground'>
                {customers.length === 0
                  ? t('customers.noCustomersFound')
                  : t('customers.noCustomersMatch')}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50'
                      onClick={() => handleSort('full_name')}
                    >
                      <div className='flex items-center'>
                        {t('customers.fullName')}
                        {getSortIcon('full_name')}
                      </div>
                    </TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50'
                      onClick={() => handleSort('phone_number')}
                    >
                      <div className='flex items-center'>
                        <Phone className='mr-1 h-3 w-3' />
                        {t('customers.phoneNumber')}
                        {getSortIcon('phone_number')}
                      </div>
                    </TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50 text-right'
                      onClick={() => handleSort('total_given')}
                    >
                      <div className='flex items-center justify-end'>
                        {t('customers.given')}
                        {getSortIcon('total_given')}
                      </div>
                    </TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50 text-right'
                      onClick={() => handleSort('total_debt')}
                    >
                      <div className='flex items-center justify-end'>
                        {t('customers.owed')}
                        {getSortIcon('total_debt')}
                      </div>
                    </TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50'
                      onClick={() => handleSort('created_at')}
                    >
                      <div className='flex items-center'>
                        {t('common.created')}
                        {getSortIcon('created_at')}
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCustomers.map((customer) => (
                    <TableRow
                      key={customer.id}
                      className='cursor-pointer'
                      onClick={() => navigate(`/customers/${customer.id}`)}
                    >
                      <TableCell>
                        <div className='font-medium'>
                          {customer.full_name ?? '-'}
                        </div>
                      </TableCell>
                      <TableCell className='text-muted-foreground'>
                        {customer.phone_number ?? '-'}
                      </TableCell>
                      <TableCell className='text-right font-medium text-green-600 dark:text-green-400'>
                        {formatCurrency(customer.total_given)}
                      </TableCell>
                      <TableCell className='text-right'>
                        <span
                          className={
                            customer.total_debt > 0
                              ? 'text-red-600 dark:text-red-400'
                              : customer.total_debt < 0
                              ? 'text-amber-600 dark:text-amber-400'
                              : 'text-muted-foreground'
                          }
                        >
                          {formatCurrency(customer.total_debt)}
                        </span>
                      </TableCell>
                      <TableCell className='text-muted-foreground'>
                        {formatDate(customer.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {/* Results count */}
            {!isLoading && customers.length > 0 && (
              <div className='mt-4 text-sm text-muted-foreground'>
                {t('common.showing')} {filteredCustomers.length} {t('common.of')} {customers.length}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}
